import { useEffect, useRef } from "react";
import mermaid from "mermaid";

/**
 * Initialise Mermaid once with a dark‑friendly palette so shapes are
 * visible on our dark chat background.
 */
mermaid.initialize({
  startOnLoad: false,
  theme: "base",
  themeVariables: {
    primaryColor: "#1d4ed8",
    primaryTextColor: "#ffffff",
    lineColor: "#ffffff",
    secondaryColor: "#1e40af",
    tertiaryColor: "#172554",
    background: "transparent",
  },
  // strict: blocks click events + javascript: URLs in diagram labels — needed
  // because chart source is rendered AI output (audit C2).
  securityLevel: "strict",
});

interface Props {
  chart: string;
}

export default function MermaidRenderer({ chart }: Props) {
  const chartId = useRef(`mermaid-${Math.random().toString(36).slice(2, 9)}`);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const renderMermaid = async () => {
      if (!containerRef.current) return;

      try {
        // Log chart source for debugging
        console.debug("Mermaid source:", chart);

        mermaid.parse(chart);

        const { svg } = await mermaid.render(chartId.current, chart);
        containerRef.current.innerHTML = svg;
      } catch (err) {
        console.error("Mermaid render error:", err);
        containerRef.current.innerHTML = `<pre class="text-red-500 whitespace-pre-wrap">${String(
          err
        )}</pre>`;
      }
    };

    renderMermaid();

    return () => {
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [chart]);

  return <div ref={containerRef} className="overflow-x-auto py-2" />;
}
